import { Brain, Compass, HeartHandshake, Gauge, FileText, LayoutDashboard } from "lucide-react";
import { Reveal, SectionTag } from "./shared";
import { useLang } from "./i18n";

const COPY = {
  en: {
    tag: "Inside the Portal", headA: "Everything the mind can tell us,", headB: "measured kindly.",
    features: [
      { Icon: Brain, color: "#1F5BFF", title: "Aptitude Battery", copy: "Five reasoning domains, adaptively calibrated to your level — never too easy, never crushing." },
      { Icon: Gauge, color: "#7C3AED", title: "Personality Profiling", copy: "Big Five trait mapping that explains how you learn, decide and thrive under pressure." },
      { Icon: Compass, color: "#3AA64B", title: "Interest Mapping", copy: "RIASEC-based inventory linking what excites you to fields where it becomes a career." },
      { Icon: HeartHandshake, color: "#E84393", title: "Emotional Intelligence", copy: "Self-awareness and empathy scores with practical guidance for classrooms and interviews." },
      { Icon: FileText, color: "#F5A524", title: "AI Career Report", copy: "A beautifully written, data-backed narrative of your strengths — drafted by AI, verified by humans." },
      { Icon: LayoutDashboard, color: "#17B6E0", title: "Counselor Dashboard", copy: "Schools and counselors track cohorts, compare clusters and intervene where it matters." },
    ],
  },
  hi: {
    tag: "पोर्टल के अंदर", headA: "मन की हर बात,", headB: "समझदारी से मापी गई।",
    features: [
      { Icon: Brain, color: "#1F5BFF", title: "योग्यता बैटरी", copy: "पाँच तर्क-क्षेत्र, आपके स्तर के अनुसार अनुकूल — न बहुत आसान, न भारी।" },
      { Icon: Gauge, color: "#7C3AED", title: "व्यक्तित्व प्रोफ़ाइल", copy: "बिग फाइव मैपिंग बताती है कि आप कैसे सीखते हैं, फ़ैसला लेते हैं और दबाव में संभलते हैं।" },
      { Icon: Compass, color: "#3AA64B", title: "रुचि मैपिंग", copy: "RIASEC-आधारित सूची आपकी रुचि को ऐसे क्षेत्रों से जोड़ती है जहाँ वह करियर बन सकती है।" },
      { Icon: HeartHandshake, color: "#E84393", title: "भावनात्मक बुद्धिमत्ता", copy: "आत्म-जागरूकता और समवेदना के स्कोर, कक्षा और इंटरव्यू के लिए व्यावहारिक सलाह के साथ।" },
      { Icon: FileText, color: "#F5A524", title: "AI करियर रिपोर्ट", copy: "आपकी ताकतों की सुंदर, डेटा-आधारित कहानी — AI द्वारा तैयार, विशेषज्ञ द्वारा जाँची गई।" },
      { Icon: LayoutDashboard, color: "#17B6E0", title: "काउंसलर डैशबोर्ड", copy: "स्कूल और काउंसलर पूरे समूह को देखें, क्लस्टर तुलना करें और सही समय पर मदद करें।" },
    ],
  },
};

export default function Features() {
  const { lang } = useLang();
  const copy = COPY[lang];
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28 sm:pb-36">
      <SectionTag color="#7C3AED">{copy.tag}</SectionTag>
      <Reveal>
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight max-w-3xl text-balance leading-[1.05]">
          {copy.headA} <span className="font-serifa italic font-normal text-iqgreen">{copy.headB}</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {copy.features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.1}>
            <div data-testid={`feature-card-${i}`} className="group rounded-3xl border border-ink/10 bg-cream p-8 h-full border-t-4 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-20px_rgba(10,22,51,0.18)]" style={{ borderTopColor: f.color }}>
              <span className="h-12 w-12 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" style={{ background: `${f.color}18` }}>
                <f.Icon className="h-[22px] w-[22px]" style={{ color: f.color }} />
              </span>
              <h3 className="font-display font-bold text-lg mt-6 tracking-tight">{f.title}</h3>
              <p className="mt-2.5 text-sm text-ink/60 leading-relaxed">{f.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
